// LEGACY: reproduces what remains of the old App.vue data orchestration —
// the unread ticket count (legacy navigation badge) and the user fetch that
// the legacy Profile view + the Datadog/Survicate identities read from Vuex.
// The document fan-out died with ADA-4435 (D6): every debt view is native
// and fetches via useDebtOverview, and the app no longer boot-gates on a
// global loader.
// Carries NO token handling: refresh = useAuth, idle = useIdleTimeout,
// cache lifecycle + logout redirect = useAuthEffects. Its only auth coupling
// is READING the identity (token_id) as a data trigger.
// Shrinks per migrated view; deleted when the last legacy view is migrated.
import { computed, inject, onBeforeUnmount, onMounted, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useQueryClient } from '@tanstack/vue-query'
import { formatRFC7231 } from 'date-fns'
import { useRoute, useRouter } from 'vue-router'

import { refreshToken } from '@/composables/useAuth'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { identifySurvicateUser } from '@/plugins/survicate'
import {
  EARLIEST_CREATED,
  isActionableState,
  mapNovaCreditBalance,
  overpaymentState,
} from '@/composables/useOverpayments'
import useAuthStore from '@/stores/authStore'
import type { NovaCreditBalance } from '@/types/nova'
import { $eventBusKey } from '@/utils/eventBus'
import { isNovaEnabled, NOVA_CREDIT_BALANCES_PATH, novaFetch } from '@/utils/novaApi'
import { invalidatePathPrefixes } from '@/utils/queryInvalidation'
import { store as legacyStoreUntyped } from '@old/store'

interface LegacyUser {
  _id?: string
  _created?: string
  email_data?: { email?: string }
  [key: string]: unknown
}

interface EveMeta {
  total?: number
  page?: string
  max_results?: number
}

interface EveList {
  _items: Record<string, unknown>[]
  _meta: EveMeta
}

interface LegacyVuexStore {
  state: {
    user: LegacyUser
    [key: string]: unknown
  }
  dispatch(action: 'set_state_property', payload: { property: string; data: unknown }): Promise<unknown>
}

const store = legacyStoreUntyped as LegacyVuexStore

// ---------------------------------------------------------------------------
// Legacy fetch helpers — ported from src_old api_mixin (hand-written fetch,
// NOT the new openapi-fetch layer). Token is read from
// authStore and refreshed via the single refresh implementation.
// ---------------------------------------------------------------------------

async function getValidToken(): Promise<string> {
  const authStore = useAuthStore()
  const exp = authStore.selectedToken?.payload?.exp
  // 30s skew, same as legacy token_helper.token_expired
  if (exp != null && Date.now() >= (exp - 30) * 1000) {
    await refreshToken()
  }
  return authStore.selectedToken?.token ?? ''
}

function buildUrl(url: string, params?: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  const path = url.startsWith('/') ? url : `/${url}`
  for (const key in params) {
    const value = params[key]
    if (typeof value === 'object' && value != null) {
      searchParams.append(key, JSON.stringify(value))
    } else {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return `${import.meta.env.VITE_APP_API_ROOT}${path}${queryString ? `?${queryString}` : ''}`
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T types the response at each call site
async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<{ status: number; data: T }> {
  const response = await fetch(buildUrl(url, params), {
    headers: {
      'X-API-Key': await getValidToken(),
      'Cache-Control': 'no-cache',
    },
  })
  if (!response.ok) {
    throw new Error(`Legacy bootstrap GET ${url} failed with ${String(response.status)}`)
  }
  const data = (await response.json()) as T
  return { status: response.status, data }
}

export function useLegacyBootstrap() {
  const authStore = useAuthStore()
  const route = useRoute()
  const router = useRouter()
  const bus = inject($eventBusKey)
  const queryClient = useQueryClient()

  function setStateProperty(property: string, data: unknown) {
    return store.dispatch('set_state_property', { property, data })
  }

  async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  // Guards resolve route.name asynchronously — wait it out (old App.vue behavior)
  async function routerInitialization() {
    while (route.name === undefined) {
      await sleep(100)
    }
  }

  // ADA-4435: the ticket views are native now — only the unread COUNT stays,
  // because the legacy navigation badge reads Vuex `unread_tickets`. The new
  // ticket mutations keep emitting `reload_ticket_data` (useTicketActions) so
  // this refreshes. Dies with the navigation migration.
  async function getUnreadTickets() {
    const response = await apiGet<EveList>('tickets', {
      where: { read_by_requester: false },
      sort: '-_updated',
      limit: 1,
    })
    await setStateProperty('unread_tickets', response.data._meta.total)
  }

  /**
   * LEGACY: the overpayment count for the navigation badge (ADA-4345).
   *
   * The view itself uses `useOverpayments`; only the badge sits in the legacy navigation, which
   * reads Vuex. The `where` below restates that composable's query filters PLUS its client-side
   * `isActionableState` verdict (server-side, since only a count is fetched) — keep the three in
   * sync BY HAND when the actionability rules move (ADA-4479 bit exactly this: booked records
   * park in `approval_status: pending/approved` with `status` still `overpayment`, and the badge
   * kept counting them). Denied bookings stay counted — the debtor can redo those. Nova credit
   * balances are added on top through the composable's own mapping and state rules (Nova has no
   * count endpoint, so the rows are fetched); an unreachable Nova counts as zero. Deleted along
   * with the rest of this bridge when the navigation migrates.
   */
  async function getUnhandledOverpayments() {
    // A blocked company session cannot act on any record, so the badge shows nothing to do.
    if (authStore.isCompanyWithoutSignatory) {
      await setStateProperty('unhandled_overpayments', 0)
      return
    }
    // Fire-and-forget callers (`void getUnhandledOverpayments()`): a failed badge refresh logs
    // once instead of surfacing as an unhandled rejection — same guard as getUserData.
    try {
      const response = await apiGet<EveList>('finance--overpayments', {
        where: {
          status: 'overpayment',
          approval_status: { $nin: ['pending', 'approved'] },
          amount_booked_as_income: { $ne: true },
          // Same window as useOverpayments (ADA-4476 B13): since the launch date, or notified by
          // letter. The legacy fetch helper below JSON.stringifies without the api layer's date
          // replacer, so the RFC 7231 formatting has to happen here.
          $or: [{ _created: { $gte: formatRFC7231(EARLIEST_CREATED) } }, { overpayment_notice_sent: true }],
        },
        limit: 1,
      })
      await setStateProperty(
        'unhandled_overpayments',
        (response.data._meta.total ?? 0) + (await getNovaUnhandledCount())
      )
    } catch (err) {
      console.error(err)
    }
  }

  async function getNovaUnhandledCount(): Promise<number> {
    if (!isNovaEnabled()) return 0
    const response = await novaFetch<{ _items: NovaCreditBalance[] }>(NOVA_CREDIT_BALANCES_PATH)
    if (!response.ok || response.data == null) return 0
    return response.data._items.filter((row) => {
      const overpayment = mapNovaCreditBalance(row)
      // Nova rows have no returned-refund children, so the state needs no list to read against.
      return overpayment != null && isActionableState(overpaymentState(overpayment, []))
    }).length
  }

  function loadTicketData() {
    void getUnreadTickets()
  }

  async function getUserData() {
    try {
      const response = await apiGet<EveList>('/customer--users')
      if (response.status === 200) {
        const [item] = response.data._items
        await setStateProperty('user', item ?? {})
      }
    } catch (err) {
      console.error(err)
    }
  }

  // (The dont_show_profile_dialog hydration moved to dialogsStore — ADA-4437 etapp 3.)

  // The ONLY auth coupling: read the identity to know when to (re)load legacy
  // data, plus legacy navigation on identity switch (dies with the old views).
  // Watches the payload (not just token_id) so token refreshes also fire —
  // needed for the user-fetch retry below.
  watch(
    () => authStore.selectedToken?.payload ?? null,
    async (newPayload, oldPayload) => {
      await routerInitialization()
      const newId = newPayload?.token_id ?? null
      const oldId = oldPayload?.token_id ?? null
      if (newId == null) return

      if (newId === oldId) {
        // Same identity, token event (e.g. hourly refresh). Legacy retried the
        // user fetch on any token event while user._id was still empty — a
        // failed first fetch must not leave the profile/analytics identity
        // empty for the whole session.
        if (store.state.user._id == null) await getUserData()
        return
      }

      if (route.name === 'Authenticate' && route.query.token) {
        // Avoid fetching documents. Allow authenticate to load new token
        return
      }

      if (route.name === 'Authenticate') {
        await router.push('/')
      } else if (oldId != null && route.name !== 'Home') {
        // Token was toggled — back to home to avoid showing information that
        // doesn't exist (multitab token sync fires this watcher in every tab).
        await router.push('/')
      }

      loadTicketData()
      void getUnhandledOverpayments()
      await getUserData()
    },
    { immediate: true }
  )

  // Datadog user context (old App.vue datadog_user_payload watcher, debounced)
  const datadogUserPayload = computed(() => {
    const payload = authStore.selectedToken?.payload
    return {
      id: payload?.u_id,
      // The customer token carries no email; it comes from the user resource.
      email: store.state.user.email_data?.email,
      name: payload?.u_name,
      solicitor: false,
    }
  })
  watchDebounced(
    datadogUserPayload,
    (value) => {
      if (value.id == null) return
      try {
        setDatadogUserAndAccount({ ...value, id: value.id })
      } catch (err) {
        console.error('Failed to set Datadog user:', err)
      }
    },
    { debounce: 500, immediate: true }
  )

  // Survicate identify — the survicate plugin owns init/identify/run
  // (replaced Wootric and its legacy component in ADA-4425)
  watch(
    () => [store.state.user._id, store.state.user.email_data?.email, store.state.user._created] as const,
    ([userId, email, created]) => {
      if (userId != null && email != null && email !== '' && created != null) {
        identifySurvicateUser({
          user_id: userId,
          email,
          created_at: new Date(created),
          solicitor: false,
        })
      }
    }
  )

  const onReloadTicketData = () => {
    loadTicketData()
  }
  // ADA-4345: same contract as the ticket counter — the navigation badge reads Vuex, so handling an
  // overpayment has to recount here. Emitted by RefundDialog once the refund has succeeded.
  const onReloadOverpayments = () => {
    void getUnhandledOverpayments()
    // Legacy → new (#63): the legacy profile page PATCHes customer--users with a raw fetch into
    // Vuex, which the query cache never sees — eligibility (the refund address is resolved from
    // the user, among other sources) and the user's own query would otherwise sit stale for the
    // full staleTime after a profile save. Invalidating here rather than in the emitter keeps
    // the bridge logic in the bridge; the refund flow's emit re-invalidates harmlessly.
    void invalidatePathPrefixes(queryClient, [
      '/customer--users',
      // The book_settlement eligibility GETs (ADA-4479 step 3.11).
      '/domain-actions/finance--overpayments/book_settlement',
      '/finance--overpayments',
    ])
  }
  onMounted(() => {
    bus?.on('reload_ticket_data', onReloadTicketData)
    bus?.on('reload_overpayments', onReloadOverpayments)
  })
  onBeforeUnmount(() => {
    bus?.off('reload_ticket_data', onReloadTicketData)
    bus?.off('reload_overpayments', onReloadOverpayments)
  })
}
